import { FunctionComponent, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  FormControlLabel,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Collapse,
  IconButton,
} from '@material-ui/core'
import { ChevronDown, ChevronUp } from 'react-feather'
import { makeStyles } from '@material-ui/styles'
import { toast } from 'react-toastify'
import axios from '../../utils/axios'
import colors from '../../colors'
import { SPACING } from '../../constants'
import useMembershipsStore from '../../stores/memberships'

const MAX_MESSAGE_LENGTH = 150

type TargetType = 'all_members' | 'recent_signups' | 'membership'

interface WalletNotification {
  id: number
  message: string
  targetType: TargetType
  targetDescription: string
  membershipId: number | null
  state: 'draft' | 'scheduled' | 'sending' | 'sent' | 'failed'
  sendAt: string | null
  sentAt: string | null
  createdAt: string
  sentBy: string | null
  recipientCount: number
  deliveredCount: number
  failedCount: number
  noDeviceCount: number
}

interface PreviewResult {
  recipientCount: number
  noDeviceCount: number
}

const WalletNotifications: FunctionComponent = () => {
  const styles = useStyles()
  const memberships = useMembershipsStore((state) => state.memberships)
  const getMemberships = useMembershipsStore((state) => state.getMemberships)

  const [message, setMessage] = useState('')
  const [targetType, setTargetType] = useState<TargetType>('all_members')
  const [membershipId, setMembershipId] = useState<string>('')
  const [isScheduled, setIsScheduled] = useState(false)
  const [sendAt, setSendAt] = useState('')

  const [preview, setPreview] = useState<PreviewResult | null>(null)
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const [notifications, setNotifications] = useState<WalletNotification[]>([])
  const [loadingHistory, setLoadingHistory] = useState(true)
  const [historyExpanded, setHistoryExpanded] = useState(true)

  useEffect(() => {
    getMemberships()
    fetchHistory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Any change to the audience/message invalidates a previous preview -- the
  // admin should always confirm counts for what they're about to actually send.
  useEffect(() => {
    setPreview(null)
  }, [message, targetType, membershipId])

  const fetchHistory = async () => {
    setLoadingHistory(true)
    try {
      const response = await axios.get('/wallet_notifications')
      setNotifications(response.data)
    } catch {
      toast.error('Failed to load notification history')
    } finally {
      setLoadingHistory(false)
    }
  }

  const buildParams = () => {
    const params: Record<string, any> = { message, target_type: targetType }
    if (targetType === 'membership') params.membership_id = membershipId
    if (isScheduled && sendAt) params.send_at = new Date(sendAt).toISOString()
    return params
  }

  const isFormValid = () => {
    if (!message.trim()) return false
    if (targetType === 'membership' && !membershipId) return false
    if (isScheduled && !sendAt) return false
    return true
  }

  const handlePreview = async () => {
    if (!isFormValid()) {
      toast.error('Fill out the message and audience before previewing')
      return
    }

    setPreviewing(true)
    try {
      const response = await axios.post('/wallet_notifications/preview', buildParams())
      setPreview(response.data)
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to preview recipients')
    } finally {
      setPreviewing(false)
    }
  }

  const handleSendClick = () => {
    if (!isFormValid()) {
      toast.error('Fill out the message and audience before sending')
      return
    }
    if (!preview) {
      toast.error('Preview recipients before sending')
      return
    }
    setConfirmOpen(true)
  }

  const handleConfirmSend = async () => {
    setSending(true)
    try {
      await axios.post('/wallet_notifications', buildParams())
      toast.success(isScheduled ? 'Notification scheduled' : 'Notification sent')
      setMessage('')
      setTargetType('all_members')
      setMembershipId('')
      setIsScheduled(false)
      setSendAt('')
      setPreview(null)
      setConfirmOpen(false)
      fetchHistory()
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send notification')
    } finally {
      setSending(false)
    }
  }

  const membershipOptions = Object.values(memberships)

  const stateColor = (state: WalletNotification['state']) => {
    switch (state) {
      case 'sent': return colors.GREEN
      case 'scheduled': return colors.YELLOW
      case 'failed': return colors.RED
      default: return colors.GREY_3
    }
  }

  return (
    <Box>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        Send a message to your members' Apple Wallet passes. Free, and it
        reaches anyone who added your card to their phone.
      </Typography>

      <Card className={`${styles.card} ${styles.composeCard}`}>
        <CardContent>
          <TextField
            label="Message"
            variant="outlined"
            fullWidth
            multiline
            minRows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, MAX_MESSAGE_LENGTH))}
            helperText={`${message.length}/${MAX_MESSAGE_LENGTH}`}
            className={styles.field}
          />

          <FormControl variant="outlined" fullWidth className={styles.field}>
            <InputLabel id="audience-label">Audience</InputLabel>
            <Select
              labelId="audience-label"
              label="Audience"
              value={targetType}
              onChange={(e) => setTargetType(e.target.value as TargetType)}
            >
              <MenuItem value="all_members">All members</MenuItem>
              <MenuItem value="recent_signups">Recent sign-ups (last 30 days)</MenuItem>
              <MenuItem value="membership">Specific membership</MenuItem>
            </Select>
          </FormControl>

          {targetType === 'membership' && (
            <FormControl variant="outlined" fullWidth className={styles.field}>
              <InputLabel id="membership-label">Membership</InputLabel>
              <Select
                labelId="membership-label"
                label="Membership"
                value={membershipId}
                onChange={(e) => setMembershipId(e.target.value as string)}
              >
                {membershipOptions.map((m: any) => (
                  <MenuItem key={m.id} value={m.id}>{m.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <FormControlLabel
            control={<Switch checked={isScheduled} onChange={(e) => setIsScheduled(e.target.checked)} />}
            label="Schedule for later"
            className={styles.field}
          />

          {isScheduled && (
            <TextField
              label="Send at"
              type="datetime-local"
              variant="outlined"
              fullWidth
              value={sendAt}
              onChange={(e) => setSendAt(e.target.value)}
              InputLabelProps={{ shrink: true }}
              className={styles.field}
            />
          )}

          <Box className={styles.actions}>
            <Button
              variant="outlined"
              onClick={handlePreview}
              disabled={previewing || !isFormValid()}
              startIcon={previewing ? <CircularProgress size={16} /> : undefined}
            >
              Preview Recipients
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSendClick}
              disabled={!preview}
            >
              {isScheduled ? 'Schedule' : 'Send'}
            </Button>
          </Box>

          {preview && (
            <Box className={styles.previewBox}>
              <Typography variant="body2">
                <strong>{preview.recipientCount}</strong> member{preview.recipientCount === 1 ? '' : 's'} will be targeted.
                {preview.noDeviceCount > 0 && (
                  <> {preview.noDeviceCount} of them haven't added their pass to Wallet and won't receive this.</>
                )}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Box
        className={styles.historyHeader}
        onClick={() => setHistoryExpanded(!historyExpanded)}
      >
        <Typography variant="h5">History</Typography>
        <IconButton size="small">
          {historyExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </IconButton>
      </Box>

      <Collapse in={historyExpanded}>
        <Card className={styles.card}>
          <CardContent>
            {loadingHistory ? (
              <CircularProgress size={24} />
            ) : notifications.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No notifications sent yet
              </Typography>
            ) : (
              <Box className={styles.tableWrapper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Message</TableCell>
                      <TableCell>Audience</TableCell>
                      <TableCell>Sent By</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Recipients</TableCell>
                      <TableCell align="right">Delivered</TableCell>
                      <TableCell align="right">Failed</TableCell>
                      <TableCell align="right">No Device</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {notifications.map((n) => (
                      <TableRow key={n.id}>
                        <TableCell className={styles.messageCell}>{n.message}</TableCell>
                        <TableCell>{n.targetDescription}</TableCell>
                        <TableCell>{n.sentBy || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={n.state.toUpperCase()}
                            size="small"
                            style={{ backgroundColor: stateColor(n.state), color: 'white', fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell align="right">{n.recipientCount}</TableCell>
                        <TableCell align="right">{n.deliveredCount}</TableCell>
                        <TableCell align="right">{n.failedCount}</TableCell>
                        <TableCell align="right">{n.noDeviceCount}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
          </CardContent>
        </Card>
      </Collapse>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm {isScheduled ? 'Schedule' : 'Send'}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will {isScheduled ? 'schedule a message to be sent' : 'immediately send a message'} to{' '}
            <strong>{preview?.recipientCount ?? 0}</strong> member{preview?.recipientCount === 1 ? '' : 's'}. This can't be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={sending}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSend}
            color="primary"
            variant="contained"
            disabled={sending}
          >
            {sending ? <CircularProgress size={20} color="inherit" /> : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    padding: SPACING * 2,
  },
  card: {
    marginBottom: SPACING * 3,
  },
  composeCard: {
    maxWidth: 900,
  },
  field: {
    marginBottom: SPACING * 2,
  },
  actions: {
    display: 'flex',
    gap: SPACING * 2,
  },
  previewBox: {
    marginTop: SPACING * 2,
    padding: SPACING * 2,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: SPACING * 2,
    cursor: 'pointer',
  },
  tableWrapper: {
    overflowX: 'auto',
  },
  messageCell: {
    maxWidth: 260,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
})

export default WalletNotifications
